import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dbke67tlu773iijbhd20";

type FormStatus = "idle" | "sending" | "success" | "error";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const subjects = [
  "Sondermaschinenbau / Vorrichtungen",
  "Stellplatzausstattung (Silver-Serie)",
  "Strom- / Wassersäulen",
  "E-Ladestationen",
  "Ersatzteile / Service",
  "Sonstiges Anliegen",
];

const inputClass =
  "box-border w-full min-w-0 border border-foreground-300 bg-background-50 px-3.5 py-3 text-[13.5px] text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-foreground-950";
const labelClass =
  "mb-2 block font-label text-[10px] uppercase tracking-[0.16em] text-foreground-500";

export default function ContactForm() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "sending") return;

    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      formEl.reset();
      return;
    }

    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();

    if (!name || !email || !message) {
      setStatus("error");
      setFormError("Bitte füllen Sie Name, E-Mail-Adresse und Nachricht aus.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      setStatus("error");
      setFormError("Bitte geben Sie eine gültige E-Mail-Adresse an.");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value !== "string") return;
      const trimmed = value.trim();
      if (trimmed !== "") payload.append(key, trimmed);
    });

    setStatus("sending");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        (parsed ? "" : responseText.slice(0, 180));

      if (!response.ok || parsed?.code !== "OK") {
        throw new Error(serverMessage || "Ihre Anfrage konnte nicht übermittelt werden.");
      }

      setStatus("success");
      setFormError("");
      formEl.reset();
    } catch (error) {
      setStatus("error");
      setFormError(
        error instanceof Error && error.message
          ? error.message
          : "Ihre Anfrage konnte nicht übermittelt werden. Bitte rufen Sie uns an.",
      );
    }
  };

  return (
    <div className="border border-foreground-200 bg-background-100 p-5 md:p-8">
      <h2 className="font-heading text-[21px] font-extrabold tracking-tightest text-foreground-950 md:text-[25px]">
        Projektanfrage
      </h2>
      <p className="mt-2 text-[13px] leading-relaxed text-foreground-600">
        Beschreiben Sie kurz, worum es geht. Wir melden uns in der Regel innerhalb eines
        Werktages.
      </p>

      <form
        id="projektanfrage-form"
        data-readdy-form
        onSubmit={handleSubmit}
        className="mt-6 flex flex-col gap-4"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="min-w-0">
            <label className={labelClass} htmlFor="name">
              Name *
            </label>
            <input
              id="name"
              name="name"
              type="text"
              required
              autoComplete="name"
              placeholder="Vor- und Nachname"
              className={inputClass}
            />
          </div>
          <div className="min-w-0">
            <label className={labelClass} htmlFor="company">
              Firma
            </label>
            <input
              id="company"
              name="company"
              type="text"
              autoComplete="organization"
              placeholder="Unternehmen / Platzbetreiber"
              className={inputClass}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="min-w-0">
            <label className={labelClass} htmlFor="email">
              E-Mail *
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              autoComplete="email"
              placeholder="name@firma.de"
              className={inputClass}
            />
          </div>
          <div className="min-w-0">
            <label className={labelClass} htmlFor="phone">
              Telefon
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              autoComplete="tel"
              placeholder="+49 …"
              className={inputClass}
            />
          </div>
        </div>

        <div className="min-w-0">
          <label className={labelClass} htmlFor="subject">
            Anliegen
          </label>
          <div className="relative">
            <select
              id="subject"
              name="subject"
              defaultValue={subjects[0]}
              className={`${inputClass} cursor-pointer appearance-none pr-10`}
            >
              {subjects.map((subject) => (
                <option key={subject} value={subject}>
                  {subject}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[16px] text-foreground-500" />
          </div>
        </div>

        <div className="min-w-0">
          <label className={labelClass} htmlFor="message">
            Nachricht *
          </label>
          <textarea
            id="message"
            name="message"
            required
            rows={5}
            maxLength={500}
            placeholder="Stückzahl, Werkstoff, Termin — oder einfach Ihr Anliegen."
            className={`${inputClass} resize-y leading-relaxed`}
          />
          <p className="mt-1.5 font-label text-[10px] uppercase tracking-[0.12em] text-foreground-400">
            Max. 500 Zeichen
          </p>
        </div>

        <input
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="hp-field"
        />

        {status === "error" && formError && (
          <div className="flex items-start gap-3 border-l-2 border-primary-600 bg-primary-50 px-4 py-3">
            <i className="ri-error-warning-line mt-0.5 text-[16px] text-primary-700" />
            <p className="text-[12.5px] leading-relaxed text-primary-900">{formError}</p>
          </div>
        )}

        {status === "success" && (
          <div className="flex items-start gap-3 border-l-2 border-primary-500 bg-background-50 px-4 py-3">
            <i className="ri-checkbox-circle-line mt-0.5 text-[16px] text-primary-600" />
            <p className="text-[12.5px] leading-relaxed text-foreground-800">
              Vielen Dank — Ihre Anfrage ist bei uns eingegangen. Wir melden uns in Kürze.
            </p>
          </div>
        )}

        <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-[260px] font-label text-[10px] leading-relaxed tracking-[0.03em] text-foreground-500">
            Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Angaben gemäß{" "}
            <Link to="/datenschutz" className="underline decoration-foreground-400 underline-offset-2 hover:text-primary-700">
              Datenschutz
            </Link>{" "}
            zu.
          </p>
          <button
            type="submit"
            disabled={status === "sending"}
            className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap bg-primary-500 px-7 py-3.5 font-heading text-[11.5px] font-bold uppercase tracking-[0.14em] text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {status === "sending" ? "Wird gesendet …" : "Anfrage senden"}
            <i className="ri-send-plane-line text-[14px]" />
          </button>
        </div>
      </form>
    </div>
  );
}